import { HomeControl } from "./home_control";

const worldBounds = [[-170, -57], [190, 78]];
// Keep the original coordinate-distance grouping, including the first marker's
// position and color, rather than changing it to screen-distance clustering.
export function groupedLocations(locations, distance) {
  const used = new Set();
  const features = [];
  locations.forEach((location, index) => {
    if (used.has(index)) return;
    used.add(index);
    const ids = [location.id];
    locations.forEach((other, otherIndex) => {
      if (!used.has(otherIndex) && Math.abs(location.lon - other.lon) < distance && Math.abs(location.lat - other.lat) < distance) {
        used.add(otherIndex);
        ids.push(other.id);
      }
    });
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [Number(location.lon), Number(location.lat)] },
      properties: { ids, count: ids.length, color: location.type === 'user' ? '#000000' : '#c32fa0' }
    });
  });
  return { type: 'FeatureCollection', features };
}

export function createDetailMap(mapboxgl, container, type, data) {
  const regions = type === 'innovation' ? data.regions : data.locations;
  const codes = type === 'innovation' ? regions.map(region => region.id) :
    ({ finland: ['FI'], australia: ['AU'], pittsburgh: ['US'] }[data.country] || regions.map(region => region.country));
  const initialBounds = worldBounds;
  const map = new mapboxgl.Map({
    container,
    style: {
      version: 8,
      glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
      sources: {},
      layers: [{ id: 'background', type: 'background', paint: { 'background-color': '#f7f7f7' } }]
    },
    bounds: initialBounds,
    fitBoundsOptions: { padding: 20 },
    projection: 'mercator',
    maxZoom: 10,
    renderWorldCopies: false,
    attributionControl: false
  });
  map.addControl(new mapboxgl.AttributionControl({ compact: true }));
  map.addControl(new mapboxgl.NavigationControl());
  map.addControl(new HomeControl(currentMap => currentMap.fitBounds(initialBounds, { padding: 20, bearing: 0, pitch: 0, linear: true, duration: 300 })));
  map.scrollZoom.disable();

  map.on('load', () => {
    container.querySelectorAll('.loader, .loading_text').forEach(element => element.remove());
    map.addLayer({
      id: type === 'innovation' ? 'innovation-countries' : 'collection-countries',
      source: { type: 'vector', url: 'mapbox://mapbox.country-boundaries-v1' },
      'source-layer': 'country_boundaries',
      type: 'fill',
      filter: ['all',
        ['==', ['get', 'disputed'], 'false'],
        ['any', ['==', ['get', 'worldview'], 'all'], ['in', 'US', ['get', 'worldview']]]
      ],
      paint: {
        'fill-color': ['case', ['in', ['get', 'iso_3166_1'], ['literal', codes.filter(Boolean)]], type === 'innovation' ? '#c32fa0' : '#DDDDDD', '#eeeeee'],
        'fill-outline-color': '#cccccc'
      }
    });
    if (type === 'innovation') {
      map.on('click', 'innovation-countries', event => {
        // Country polygons span multiple vector tiles; their tile bounds are
        // unsuitable for framing. Use the same country bounds as the main map.
        const country = event.features[0].properties.iso_3166_1_alpha_3;
        const coordinates = data.countryBounds[country];
        if (coordinates) map.fitBounds([[coordinates.sw.lon, coordinates.sw.lat], [coordinates.ne.lon, coordinates.ne.lat]], { padding: 20 });
      });
    } else {
      const distance = data.country && data.country !== 'global' ? 0.5 : 4.5;
      const grouped = groupedLocations(data.locations, distance);
      const detailed = groupedLocations(data.locations, 0.5);
      const initialZoom = map.getZoom();
      let expanded = false;
      map.addSource('objects', { type: 'geojson', data: grouped });
      map.addLayer({
        id: 'collection-markers', type: 'circle', source: 'objects',
        paint: {
          'circle-color': ['get', 'color'],
          'circle-radius': ['case', ['>', ['get', 'count'], 1], 12, 6]
        }
      });
      map.addLayer({
        id: 'collection-counts', type: 'symbol', source: 'objects',
        filter: ['>', ['get', 'count'], 1],
        layout: { 'text-field': ['to-string', ['get', 'count']], 'text-size': 12, 'text-allow-overlap': true },
        paint: { 'text-color': '#ffffff' }
      });
      map.on('click', 'collection-markers', event => {
        const feature = event.features[0];
        const ids = typeof feature.properties.ids === 'string' ? JSON.parse(feature.properties.ids) : feature.properties.ids;
        if (ids.length > 1 && map.getZoom() < initialZoom + 2 - 0.01) {
          map.easeTo({ center: feature.geometry.coordinates, zoom: initialZoom + 2 });
        } else {
          data.onSelect(event, ids.map(id => ({ properties: { id } })));
        }
      });
      map.on('mouseenter', 'collection-markers', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'collection-markers', () => { map.getCanvas().style.cursor = ''; });
      map.on('zoomend', () => {
        const zoom = map.getZoom() - initialZoom;
        const next = zoom >= 2 - 0.01 ? true : zoom < Math.log2(3) ? false : expanded;
        if (next !== expanded) {
          expanded = next;
          map.getSource('objects').setData(expanded ? detailed : grouped);
        }
      });
    }
  });
  return map;
}
